import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { FiEdit3, FiEye } from "react-icons/fi";
import { FaHistory } from "react-icons/fa";
import { GrDocumentDownload } from "react-icons/gr";

import ImageInput from "app/Components/ImageInput/ImageInput";
import CustomTooltip from "app/Components/CommanComponents/CustomTooltip";
import SearchBootstrapTable from "app/Components/BootstrapTable/SearchBootstrapTable";
import { useToggle } from "app/_helpers/hooks";
import { formatDDMMYYYYbyYYYYMMDD } from "app/_helpers/helper";
import { getAllEmployeeFormAHistoryByID } from "app/_services/UserManagement/EmployeeService";
import { getAllContractorPersonFormAHistoryByID } from "app/_services/UserManagement/ContractorPersonService";
import { EFormFieldInputTypes } from "app/Components/Form/FormField.component";
import { SetPermission } from "app/Components/SetPermission/Permissions";

const formAContractorNameField = {
  dataField: "contractorPerson.contractors.contactPerson",
  text: "Contractor",
};

const formAHistoryColumns = [
  {
    dataField: "sId",
    text: "S.No.",
  },
  {
    dataField: "staffTypeName",
    text: "Staff Type",
  },
  {
    dataField: "site.name",
    text: "Location",
  },
  {
    dataField: "createdAt",
    text: "Created Date",
    formatter: (value) => formatDDMMYYYYbyYYYYMMDD(value),
  },
  {
    dataField: "id",
    text: "Action",
    formatter: (rowId, rowData) => {
      const baseUrl =
        rowData.staffType === "employee"
          ? "/user-management/employee"
          : "/user-management/contractor-person";
      return (
        <div className="actionIconsGroup">
          <CustomTooltip tooltip="View">
            <Link
              to={{
                pathname: `${baseUrl}/form-a-history/` + rowId,
                state: {
                  backUrl: window.location.pathname,
                },
              }}
              className="icons btn-icon font-20 text-cyanBlue pointer"
            >
              <FiEye />
            </Link>
          </CustomTooltip>
        </div>
      );
    },
  },
];

export const FormAHistory = ({ staffTypeName, id }) => {
  const [show, toggleShow] = useToggle(false);
  const [rows, setRows] = useState([]);
  const fetched = useRef(false);
  const columns = useMemo(() => {
    const cols = [...formAHistoryColumns];

    if (staffTypeName !== "Employee")
      cols.splice(cols.length - 2, 0, formAContractorNameField);

    return cols;
  }, [staffTypeName]);

  useEffect(() => {
    if (!show || fetched.current) return;

    (staffTypeName === "Employee"
      ? getAllEmployeeFormAHistoryByID(id)
      : getAllContractorPersonFormAHistoryByID(id)
    ).then((response) => {
      if (!Array.isArray(response.data?.data) && !Array.isArray(response.data)) return;

      const newRows = (
        Array.isArray(response.data) ? response.data : response.data?.data
      ).map((row, i) => ({ ...row, staffTypeName, sId: i + 1 }));

      fetched.current = !!newRows.length;

      setRows(newRows);
    });
  }, [show, staffTypeName, id]);

  return (
    <>
      <CustomTooltip tooltip="Form A History">
        <span
          className="icons btn-icon font-20 text-warning pointer"
          onClick={toggleShow}
        >
          <FaHistory />
        </span>
      </CustomTooltip>
      <Modal show={show} onHide={toggleShow} size="lg">
        <div className="card p-3 pb-0 mt-0 EHS_module_create_form">
          <div className="d-flex justify-content-between simple-card-title">
            <h3 className="text-capitalize mb-0">{`${
              staffTypeName || ""
            } Form A History`}</h3>
          </div>
          <div className="card-body">
            <SearchBootstrapTable columns={columns} data={rows} tableName="Form A" />
          </div>
        </div>
      </Modal>
    </>
  );
};

export const formAColumn = (isFormAList = false) => {
  return {
    dataField: "formAdata",
    text: "Form A Data",
    sort: true,
    formatter: (rowId, rowData) => {
      const baseUrl =
        rowData.staffType === "employee"
          ? "/user-management/employee"
          : "/user-management/contractor-person";
      return (
        <div className="actionIconsGroup">
          {rowData?.ehsSite?.siteType !== 3 && isFormAList ? (
            <FormAHistory {...rowData} />
          ) : rowData?.ehsSite?.siteType !== 3 ? (
            "N/A"
          ) : (
            <>
              <SetPermission
                permission={["superAdmin", "globalAdmin", "Form A Editor"]}
                data={
                  <>
                    {isFormAList && <FormAHistory {...rowData} />}
                    {rowData?.employmentStatus === "active" && (
                      <CustomTooltip tooltip="Edit">
                        <Link
                          to={{
                            pathname:
                              `${baseUrl}/edit-form-a/` +
                              rowData?.id +
                              "?backUrl=" +
                              encodeURIComponent(window.location.pathname),
                            state: {
                              backUrl: window.location.pathname,
                            },
                          }}
                          className="icons btn-icon font-20 text-cyanBlue"
                        >
                          <FiEdit3 />
                        </Link>
                      </CustomTooltip>
                    )}
                  </>
                }
              />
              <SetPermission
                permission={[
                  "superAdmin",
                  "projectAdmin",
                  "globalAdmin",
                  "Form A Viewer",
                  "Form A Editor",
                ]}
                data={
                  <>
                    <CustomTooltip tooltip="View">
                      <Link
                        to={{
                          pathname: `${baseUrl}/download-form-a/` + rowData?.id,
                          //  +
                          // "?backUrl=" +
                          // encodeURIComponent(window.location.pathname)
                          state: {
                            backUrl: window.location.pathname,
                          },
                        }}
                        className="icons btn-icon font-20 text-cyanBlue pointer"
                      >
                        <GrDocumentDownload />
                      </Link>
                    </CustomTooltip>
                  </>
                }
              />
            </>
          )}
        </div>
      );
    },
  };
};

const mineSectionFields = [
  {
    title: "Mine Name",
    name: "mineName",
    readOnly: true,
    required: true,
    colLgView: 4,
    colMdView: 4,
  },
  {
    title: "Mine Code (MOL)",
    name: "mineCode",
    readOnly: true,
    required: true,
    colLgView: 4,
    colMdView: 4,
  },
  {
    title: "Name of the Lesse",
    name: "mineLesseName",
    readOnly: true,
    required: true,
    colLgView: 4,
    colMdView: 4,
  },
];

const mineDetailsSection = {
  title: "Mine Details",
  fields: mineSectionFields,
  hideSectionInView: true,
};

export const mineDetailsSectionView = {
  title: "Mine Details",
  name: "mineDetailsSectionView",
  hideTitleInView: true,
  fields: [
    {
      name: "mineDetailsSection",
      inputType: "section",
      fields: mineSectionFields,
      hideTitleInView: true,
    },
  ],
  inputType: "section",
};

const profilePictureField = {
  title: "Profile Picture",
  required: false,
  name: "profilePicture",
  inputType: "image",
  colLgView: 2,
  colMdView: 2,
  width: 100,
};

const staffTypeField = {
  title: "Staff-Type",
  required: true,
  inputType: "searchable-select",
  name: "staffType",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
  readOnly: true,
  options: [
    {
      label: "Employee",
      value: "employee",
    },
    {
      label: "Contractor",
      value: "contractor",
    },
    {
      label: "Others",
      value: "others",
    },
  ],
};

const contractorNameField = {
  title: "Name of the Contractor",
  required: true,
  name: "contractorName",
  readOnly: true,
  staffType: "contractor",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const contractorCodeField = {
  title: "Contractor Code",
  required: true,
  name: "contractorCode",
  readOnly: true,
  staffType: "contractor",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const serialNumberField = {
  title: "Serial No.",
  required: true,
  name: "srNo",
  inputType: "text",
  readOnly: true,
  staffType: "contractor",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const employeeCodeField = {
  title: "Employee Code",
  required: true,
  name: "employeeId",
  readOnly: true,
  staffType: "employee",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const serialNumberOmcField = {
  title: "Serial No. (OMC Employees)",
  required: true,
  name: "serialNumberOmc",
  staffType: "employee",
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const fullNameField = {
  title: "Name",
  required: true,
  name: "fullName",
  readOnly: true,
  colLg: 6,
  colMd: 6,
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const genderField = {
  title: "Gender",
  required: true,
  name: "gender",
  inputType: "searchable-select",
  options: [
    { name: "Male", value: "male" },
    { name: "Female", value: "female" },
    { name: "Other", value: "other" },
  ],
  readOnly: true,
  colLg: 6,
  colMd: 6,
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const guardianTypeField = {
  title: "Guardian Type",
  name: "guardianType",
  inputType: "searchable-select",
  required: true,
  options: [
    {
      label: "Father",
      value: "father",
    },
    {
      label: "Spouse",
      value: "spouse",
    },
  ],
  colLg: 6,
  colMd: 6,
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const fathersNameField = {
  title: (form) =>
    !form[guardianTypeField.name]
      ? "Father's / Spouse Name"
      : form[guardianTypeField.name] === "spouse"
      ? "Spouse Name"
      : "Father's Name",
  required: true,
  name: "fathersName",
  colLg: 6,
  colMd: 6,
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const dobField = {
  title: "Date of Birth",
  required: true,
  name: "dob",
  readOnly: true,
  colLg: 6,
  colMd: 6,
  colLgView: 4,
  colMdView: 4,
  colSmView: 6,
  colView: 12,
};

const nationalityField = {
  title: "Nationality",
  required: true,
  name: "nationality",
  inputType: "searchable-select",
  options: [
    {
      label: "Indian",
      value: "indian",
    },
    {
      label: "Others",
      value: "others",
    },
  ],
  colLg: 6,
  colMd: 6,
};

const educationLevelField = {
  title: "Education Level",
  required: true,
  name: "educationLevel",
  colLg: 6,
  colMd: 6,
  colLgView: 12,
  colMdView: 12,
  colSmView: 12,
  colView: 12,
};

const identificationMarkField = {
  title: "Mark for Identification",
  name: "identificationMark",
  required: true,
  colLg: 6,
  colMd: 6,
  colLgView: 12,
  colMdView: 12,
  colSmView: 12,
  colView: 12,
};

const personalDetailsFields = [
  {
    name: "details",
    inputType: "section",
    colLg: 8,
    colMd: 8,
    colSm: 12,
    colLgView: 8,
    colMdView: 8,
    fields: [fullNameField, genderField, dobField, nationalityField],
  },
  profilePictureField,
  // {
  //   name: "employmentDetails",
  //   inputType: "section",
  //   fields: [staffTypeField, contractorNameField, contractorCodeField],
  // },
  guardianTypeField,
  fathersNameField,
  educationLevelField,
  {
    title: "Mobile No. of Employee",
    name: "mobileNumber",
    colLg: 6,
    colMd: 6,
    readOnly: true,
  },
  {
    title: "Languages Known",
    name: "languagesKnown",
    colLg: 12,
    colMd: 12,
  },
];
export const personalDetailsFieldsView = [
  {
    name: "personalDetails",
    inputType: "section",
    fields: [
      staffTypeField,
      contractorNameField,
      contractorCodeField,
      serialNumberField,
      employeeCodeField,
      serialNumberOmcField,
      ...mineSectionFields.map((f) => ({ ...f, staffType: "employee" })),
    ],
    colLgView: 10,
    colMdView: 10,
  },
  { ...profilePictureField, title: "" },
  {
    inputType: "section",
    name: "personalDetails2",
    fields: [
      fullNameField,
      genderField,
      dobField,
      guardianTypeField,
      fathersNameField,
      nationalityField,
      {
        title: "Languages Known",
        name: "languagesKnown",
        colLg: 12,
        colMd: 12,
      },
    ],
    colLgView: 10,
    colMdView: 10,
  },
  {
    inputType: "section",
    name: "personalDetails3",
    fields: [educationLevelField, identificationMarkField],
    colLgView: 2,
    colMdView: 2,
  },
];

export const personalDetailsSection = {
  title: "Personal Info",
  hideSectionInView: true,
  hideTitleInView: true,
  fields: personalDetailsFields,
};

export const formAFields = {
  mineDetailsSection,
  employeeSrId: {
    title: "Employee & Serial ID",
    fields: [serialNumberField, employeeCodeField, serialNumberOmcField],
  },
  personalDetailsSection,
  employmentDetails: {
    title: "Employment Info",
    fields: [
      contractorNameField,
      contractorCodeField,
      {
        title: "Place of Employment",
        inputType: "searchable-select",
        name: "employmentPlace",
        required: true,
        options: [
          {
            label: "UG",
            value: "ug",
          },
          {
            label: "OC",
            value: "oc",
          },
          {
            label: "Surface",
            value: "surface",
          },
        ],
      },
      {
        title: "Date of First appointment with present owner",
        name: "startDateAtMines",
        required: true,
        readOnly: true,
        colLgView: 4,
        colMdView: 4,
      },
      {
        title: "Date of Joining in present designation",
        name: "dateOfJoining",
        required: true,
        readOnly: true,
        colLgView: 4,
        colMdView: 4,
      },
      {
        title: "Designation",
        name: "designation",
        required: true,
        readOnly: true,
      },
      {
        title: "Category Address",
        inputType: "searchable-select",
        name: "categoryAddress",
        required: true,
        options: [
          {
            label: "ST",
            value: "st",
          },
          {
            label: "SC",
            value: "sc",
          },
          {
            label: "OBC",
            value: "obc",
          },
          {
            label: "UR",
            value: "ur",
          },
        ],
      },
      {
        title: "Type of Employment",
        inputType: "searchable-select",
        name: "typeOfEmployment",
        required: true,
        options: [
          {
            label: "Unskilled",
            value: "unskilled",
          },
          {
            label: "Semi-skilled",
            value: "semi-skilled",
          },
          {
            label: "Skilled",
            value: "skilled",
          },
        ],
      },
    ],
  },
  identificationNumbers: {
    title: "Identification Numbers",
    fields: [
      {
        title: "UAN-Universal Account Number",
        name: "uan",
        required: true,
        inputType: "number",
        min: 0,
        validate: (value) =>
          value && !/^([0-9]){12}?$/.test(value)
            ? "Please enter a valid UAN Number."
            : "",
      },
      {
        title: "PAN",
        name: "panCard",
        required: true,
        validate: (value) =>
          value && !/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/.test(value)
            ? "Please enter a valid PAN Card Number."
            : "",
      },
      {
        title: "ESIC IP",
        name: "esicIp",
        required: true,
        inputType: "number",
        min: 0,
      },
      {
        title: "LWF-Labour welfare fund",
        name: "labourWelfareFund",
        required: true,
        inputType: "number",
        min: 0,
      },
      {
        title: "Aadhaar Number",
        name: "aadharNumber",
        required: true,
        readOnly: true,
      },
      {
        title: "Service Book No.",
        name: "serviceBookNumber",
        required: true,
        inputType: "number",
        min: 0,
        validate: (value) =>
          value && !/^([0-9]){6}?$/.test(value) ? "Please enter a valid Pin Number." : "",
      },
      {
        title: "Token Number Issued",
        name: "issuedTokenNumber",
        required: true,
        inputType: "number",
        min: 0,
      },
    ],
  },
  bankDetails: {
    title: "Bank Account Details",
    // hideTitleInView: true,
    fields: [
      {
        title: "Bank A/c Number",
        name: "accountNumber",
        required: true,
        inputType: "number",
      },
      {
        title: "Bank Name",
        name: "bankName",
        required: true,
        inputType: "string",
      },
      {
        title: "Branch (IFSC)",
        name: "ifsc",
        required: true,
      },
    ],
  },
  contactDetails: {
    title: "Addresses",
    fields: [
      {
        name: "presentAddressDetails",
        title: "Present Address",
        inputType: "section",
        customView: ({
          presentAddressLineOne,
          presentAddressLineTwo,
          presentCity,
          presentDistrict,
          presentPinCode,
        }) => (
          <ViewFieldContainer colLgView={4} colMdView={4} colSmView={12} colView={12}>
            <AddressView
              title="Present Address"
              lineOne={presentAddressLineOne}
              lineTwo={presentAddressLineTwo}
              city={presentCity}
              district={presentDistrict}
              pinCode={presentPinCode}
            />
          </ViewFieldContainer>
        ),
        fields: [
          {
            title: "Line 1 / Village",
            name: "presentAddressLineOne",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Line 2 / Post Office",
            name: "presentAddressLineTwo",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "City",
            name: "presentCity",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "District",
            name: "presentDistrict",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "State",
            name: "presentState",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },

          {
            title: "Pin Code",
            name: "presentPinCode",
            required: true,
            inputType: "number",
            min: 0,
            colLg: 3,
            colMd: 6,
            colSm: 12,
            validate: (value) =>
              value && !/^([0-9]){6}?$/.test(value)
                ? "Please enter a valid Pin Number Number."
                : "",
          },
        ],
      },
      {
        name: "currentAddress",
        title: "Permanent Address",
        inputType: "section",
        customView: ({
          currentAddressLineOne,
          currentAddressLineTwo,
          currentCity,
          currentDistrict,
          currentPinCode,
        }) => (
          <ViewFieldContainer colLgView={4} colMdView={4} colSmView={12} colView={12}>
            <AddressView
              title="Permanent Address"
              lineOne={currentAddressLineOne}
              lineTwo={currentAddressLineTwo}
              city={currentCity}
              district={currentDistrict}
              pinCode={currentPinCode}
            />
          </ViewFieldContainer>
        ),
        fields: [
          {
            title: "Line 1 / Village",
            name: "currentAddressLineOne",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Line 2 / Post Office",
            name: "currentAddressLineTwo",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "City",
            name: "currentCity",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "District",
            name: "currentDistrict",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "State",
            name: "currentState",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Pin Code",
            name: "currentPinCode",
            required: true,
            inputType: "number",
            min: 0,
            colLg: 3,
            colMd: 6,
            colSm: 12,
            validate: (value) =>
              value && !/^([0-9]){6}?$/.test(value)
                ? "Please enter a valid Pin Number Number."
                : "",
          },
        ],
      },
    ],
  },
  trainingDetails: {
    title: "Certificate Nos",
    // hideTitleInView: true,
    fields: [
      {
        name: "fitnessDetails",
        title: "Fitness Details",
        hideTitleInView: true,
        inputType: "section",
        fields: [
          {
            title: "Certificate of Fitness - IME No.",
            name: "fitnessCertificateImeNumber",
            required: true,
            min: 0,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
          },
          {
            title: "Certificate of Fitness - IME Date",
            name: "fitnessCertificateImeDate",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
          },
          {
            title: "Certificate of Fitness - PME No.",
            name: "fitnessCertificatePmeNumber",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
            min: 0,
          },
          {
            title: "Certificate of Fitness - PME Date",
            name: "fitnessCertificatePmeDate",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
          },
        ],
      },
      {
        name: "trainingDetails",
        title: "Training Details",
        hideTitleInView: true,
        inputType: "section",
        fields: [
          {
            title: "Certificate of Initial Training - No.",
            name: "initialTrainingCertificateNumber",
            required: true,
            readOnly: true,
            min: 0,
            colLgView: 3,
            colMdView: 6,
          },
          {
            title: "Certificate of Initial Training Date",
            name: "initialTrainingCertificateDate",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
          },
          {
            title: "Certificate of Refresher Training - No",
            name: "refresherCertificateNumber",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
            min: 0,
          },
          {
            title: "Certificate of Refresher Training - Date",
            name: "refresherCertificateDate",
            required: true,
            readOnly: true,
            colLgView: 3,
            colMdView: 6,
          },
        ],
      },
    ],
  },
  nomineeDetails: {
    title: "Nominee Details",
    fields: [
      {
        title: "Nominee Name",
        name: "nomineeName",
        required: true,
        colLg: 6,
        colMd: 6,
      },
      {
        title: "Nominee Relationship",
        name: "nomineeRelationship",
        required: true,
        colLg: 6,
        colMd: 6,
      },
      {
        name: "nomineeAddressDetails",
        title: "Address Details",
        inputType: "section",
        customView: ({
          nomineeAddressLineOne,
          nomineeAddressLineTwo,
          nomineeCity,
          nomineeDistrict,
          nomineePinCode,
        }) => (
          <ViewFieldContainer colLgView={4} colMdView={4} colSmView={12} colView={12}>
            <AddressView
              title="Nominee Address"
              lineOne={nomineeAddressLineOne}
              lineTwo={nomineeAddressLineTwo}
              city={nomineeCity}
              district={nomineeDistrict}
              pinCode={nomineePinCode}
            />
          </ViewFieldContainer>
        ),
        fields: [
          {
            title: "Line 1",
            name: "nomineeAddressLineOne",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Line 2",
            name: "nomineeAddressLineTwo",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "City",
            name: "nomineeCity",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "District",
            name: "nomineeDistrict",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "State",
            name: "nomineeState",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Pin Code",
            name: "nomineePinCode",
            required: true,
            inputType: "number",
            min: 0,
            colLg: 3,
            colMd: 6,
            colSm: 12,
            validate: (value) =>
              value && !/^([0-9]){6}?$/.test(value)
                ? "Please enter a valid Pin Number Number."
                : "",
          },
        ],
      },
    ],
  },
  emergencyContactDetails: {
    title: "Emergency Contact",
    fields: [
      {
        title: "Name of Person to be contacted in an emergency",
        name: "emergencyContactName",
        required: true,
        colLg: 6,
        colMd: 6,
        colLgView: 4,
        colMdView: 4,
      },
      {
        title: "Relationship",
        name: "emergencyContactRelationship",
        required: true,
        colLg: 6,
        colMd: 6,
        colLgView: 4,
        colMdView: 4,
      },
      {
        name: "emergencyAddressDetails",
        title: "Address Details",
        inputType: "section",
        customView: ({
          emergencyContactAddressLineOne,
          emergencyContactAddressLineTwo,
          emergencyContactCity,
          emergencyContactDistrict,
          emergencyContactPinCode,
        }) => (
          <ViewFieldContainer colLgView={4} colMdView={4} colSmView={12} colView={12}>
            <AddressView
              title="Emergency Contact Address"
              lineOne={emergencyContactAddressLineOne}
              lineTwo={emergencyContactAddressLineTwo}
              city={emergencyContactCity}
              district={emergencyContactDistrict}
              pinCode={emergencyContactPinCode}
            />
          </ViewFieldContainer>
        ),
        fields: [
          {
            title: "Line 1",
            name: "emergencyContactAddressLineOne",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "Line 2",
            name: "emergencyContactAddressLineTwo",
            required: true,
            colLg: 6,
            colMd: 6,
            colSm: 12,
          },
          {
            title: "City",
            name: "emergencyContactCity",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 6,
          },
          {
            title: "District",
            name: "emergencyContactDistrict",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 6,
          },
          {
            title: "State",
            name: "emergencyContactState",
            required: true,
            colLg: 3,
            colMd: 6,
            colSm: 6,
          },
          {
            title: "Pin Code",
            name: "emergencyContactPinCode",
            required: true,
            inputType: "number",
            min: 0,
            colLg: 3,
            colMd: 6,
            colSm: 6,
            validate: (value) =>
              value && !/^([0-9]){6}?$/.test(value)
                ? "Please enter a valid Pin Number Number."
                : "",
          },
        ],
      },
    ],
  },
  identificationMark: {
    title: "Identification Mark",
    fields: [identificationMarkField],
  },
  exitDetails: {
    title: "Exit Details",
    fields: [
      {
        title: "Date of Exit",
        name: "dateOfSeparation",
        required: true,
        readOnly: true,
        colLg: 6,
        colMd: 6,
        colLgView: 6,
        colMdView: 6,
      },
      {
        title: "Reason of Exit",
        name: "employmentStatus",
        required: true,
        readOnly: true,
        colLg: 6,
        colMd: 6,
        colLgView: 6,
        colMdView: 6,
      },
    ],
  },
  remarks: {
    title: "Remarks",
    fields: [
      {
        title: "Remarks",
        name: "remarks",
        required: true,
        inputType: EFormFieldInputTypes.textarea,
        colLg: 12,
        colMd: 12,
        colLgView: 12,
        colMdView: 12,
      },
    ],
  },
};

export function ViewFieldContainer({
  colLgView = 4,
  colMdView = 4,
  colSmView = 6,
  colView = 6,
  children,
  noBorder = false,
}) {
  return (
    <div
      className={`col-lg-${colLgView} col-md-${colMdView} col-sm-${colSmView} col-${colView} my-0${
        noBorder ? "" : " border"
      } py-0`}
      style={{ minHeight: 70 }}
    >
      {children}
    </div>
  );
}

export function ViewField({
  colLgView,
  colMdView,
  colSmView,
  colView,
  title,
  data,
  name,
  inputType,
  height,
  width,
}) {
  return (
    <ViewFieldContainer
      colLgView={colLgView}
      colMdView={colMdView}
      colSmView={colSmView}
      colView={colView}
    >
      <div className="my-2">
        {title && (
          <FieldTitle title={typeof title === "function" ? title(data) : title} />
        )}
        {inputType === "image" ? (
          <ImageInput
            name={name}
            value={data[name]}
            height={height}
            width={width}
            disabled
          />
        ) : (
          <h6>{data[name]}</h6>
        )}
      </div>
    </ViewFieldContainer>
  );
}

function AddressView({ title = "Address", lineOne, lineTwo, city, district, pinCode }) {
  return (
    <div className="my-2">
      <FieldTitle title={title} />
      <h6>{`${lineOne ? lineOne + ", " : ""} ${lineTwo ? lineTwo + ", " : ""} ${
        city ? city + ", " : ""
      } ${district ? district + ", " : ""} Pincode: ${pinCode || ""}`}</h6>
    </div>
  );
}

function FieldTitle({ title }) {
  return (
    <h6>
      <strong>{title}</strong>
    </h6>
  );
}
